import { computed, inject } from 'vue';
import { type FieldMeta, PublicFormContextKey, useFieldError, useFormContext } from 'vee-validate';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { isString } from '@cvo/domain/utils';
import type { IconName } from '../components';

export interface ValidationRule {
  required?: boolean;
  custom?: (value: unknown) => boolean | string;
}

export interface FieldProps {
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** Readonly input: submitted but can't be edited by the user. */
  static?: boolean;
  /** `false` disables field error. `true` will automatically pick error from vee-validate schema. `string` will show the given parameter as error message. */
  error?: string | boolean;
  helperText?: string;
  hideAsterisk?: boolean;
}

/**
 * Merge Field components props with vee-validate field validation
 */
export function useFieldValidation(fieldProps: FieldProps) {
  const { t } = useI18n();

  const hasForm = inject(PublicFormContextKey, null) != null;

  const formContext = hasForm ? useFormContext() : null;
  // @ts-expect-error Temporary hack to use internal api to get the "required" meta data. It's deprecated in V5.
  const fieldState: FieldMeta<unknown> | undefined = formContext?.getPathState(fieldProps.name);

  const fieldError = useFieldError(() => fieldProps.name ?? '');

  // TODO In vee-validate v5, we won't be able to retrieve the "required" meta data anymore. All field will have to explicitely set the required prop.
  const isRequired = computed<boolean>(() => fieldProps.required || fieldState?.required || false);

  const translatedFieldError = computed<string | undefined>(() => {
    return fieldError?.value ? t(fieldError?.value) : undefined;
  });

  const errorMessage = computed<string | undefined>(() => {
    if (fieldProps.error === false) return undefined;
    if (isString(fieldProps.error)) return fieldProps.error;

    return translatedFieldError.value;
  });

  const hasError = computed(() => fieldProps.error === true || Boolean(errorMessage.value));
  const hasHelper = computed(() => hasError.value || Boolean(fieldProps.helperText));

  const helperIconName = computed((): IconName => {
    return !!errorMessage.value ? 'alert-circle' : 'information';
  });

  return {
    hasError,
    hasHelper,
    errorMessage,
    isRequired,
    helperIconName,
    hideAsterisk: fieldProps.hideAsterisk,
  };
}